import { useState, useEffect } from 'react';
import {
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    CircularProgress,
    InputAdornment,
    Alert,
    Checkbox,
    FormControlLabel,
    Box,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
    paymentsApi,
    CaProduct,
    CaPaymentMethod,
    MfiCenter,
    CreateInvoiceResponse,
    MfiEventType,
} from '../services/payments-api.service';
import { CenterSelect } from './CenterSelect';
import { useAuth } from '../contexts/AuthContext';

interface InvoiceFormData {
    email: string;
    firstName: string;
    lastName: string;
    locale: string;
    centerSlugId: string;
    teacherName: string;
    productSku: string;
    paymentMethod: string;
    startDate: string;
    numberOfDays: number;
    quantity: number;
    amount: string;
    leadSource: string;
    memo: string;
    taxExempt: boolean;
    sendEmail: boolean;
    vedicExpert: string;
}

interface InvoiceFormProps {
    onSuccess?: (response: CreateInvoiceResponse) => void;
}

export function InvoiceForm({ onSuccess }: InvoiceFormProps) {
    const today = new Date().toISOString().split('T')[0];
    const initialFormData: InvoiceFormData = {
        email: '',
        firstName: '',
        lastName: '',
        locale: 'en-CA',
        centerSlugId: '',
        teacherName: '',
        productSku: '',
        paymentMethod: '',
        startDate: today,
        numberOfDays: 1,
        quantity: 1,
        amount: '',
        leadSource: '',
        memo: '',
        taxExempt: false,
        sendEmail: true,
        vedicExpert: '',
    };

    const [formData, setFormData] = useState<InvoiceFormData>(initialFormData);
    const [products, setProducts] = useState<CaProduct[]>([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [selectedCenter, setSelectedCenter] = useState<MfiCenter | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<CreateInvoiceResponse | null>(null);

    const { role } = useAuth();

    const teacherProductSkus = ['Donation', 'AT-Deposit', 'MLG'];

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                let products: CaProduct[] = [];
                if (role === 'teacher') {
                    // Limit teachers to products that cannot billed via other mechanisms (e.g. MFI dashboard)
                    const result = await paymentsApi.getProducts();
                    products = result.products.filter((p) => teacherProductSkus.includes(p.productSku));
                } else {
                    const result = await paymentsApi.getProducts();
                    products = result.products;
                }
                setProducts(products);
            } catch (err) {
                console.error('Failed to load products:', err);
            } finally {
                setProductsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const handleChange = (field: keyof InvoiceFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSelectChange = (field: keyof InvoiceFormData) => (e: SelectChangeEvent) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleProductChange = (e: SelectChangeEvent) => {
        const productSku = e.target.value;
        setFormData((prev) => ({
            ...prev,
            productSku: productSku || '',
            paymentMethod: '',
        }));
    };

    const handlePaymentMethodChange = (e: SelectChangeEvent) => {
        const paymentMethod = e.target.value;
        setFormData((prev) => ({
            ...prev,
            paymentMethod,
            // Default sendEmail to false for 'account' payment method
            sendEmail: paymentMethod === CaPaymentMethod.account ? false : true,
        }));
    };

    const selectedProduct = products.find((p) => p.productSku === formData.productSku);
    const availablePaymentMethods = selectedProduct?.paymentMethods || [];

    const formatPaymentMethod = (method: string) =>
        method
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        try {
            // Get the selected center to get the taxState
            if (!selectedCenter) {
                throw new Error('Please select a centre');
            }

            const request = {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                locale: formData.locale,
                taxState: selectedCenter.state,
                taxExempt: formData.taxExempt,
                paymentMethod: formData.paymentMethod as CaPaymentMethod,
                sendEmail: formData.sendEmail,
                items: [
                    {
                        productSku: formData.productSku,
                        startDate: formData.startDate,
                        numberOfDays: formData.numberOfDays,
                        amount: Math.round((parseFloat(formData.amount) * 100) / formData.quantity), // Convert to cents and calculate per item amount
                        quantity: formData.quantity,
                    },
                ],
                metadata: {
                    teacherName: formData.teacherName,
                    centerSlugId: formData.centerSlugId,
                    ...(formData.leadSource && { leadSource: formData.leadSource }),
                    ...(formData.memo && { memo: formData.memo }),
                    ...(formData.vedicExpert && { vedicExpert: formData.vedicExpert }),
                },
            };

            const result = await paymentsApi.createInvoice(request);
            setSubmitSuccess(result);
            onSuccess?.(result);

            // Clear form data after successful submission
            setFormData(initialFormData);
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : (err as { message?: string })?.message || 'Failed to create invoice';
            setSubmitError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const taxExemptValidationError = formData.taxExempt && !formData.memo.trim();

    const requiresVedicExpert =
        selectedProduct?.eventType === MfiEventType.tmAdvancedTechnique ||
        selectedProduct?.eventType === MfiEventType.tmSidhisCourse;

    const isFormValid =
        formData.email &&
        formData.firstName &&
        formData.lastName &&
        formData.locale &&
        formData.centerSlugId &&
        formData.teacherName &&
        formData.productSku &&
        formData.paymentMethod &&
        formData.startDate &&
        (!requiresVedicExpert || formData.vedicExpert.trim()) &&
        formData.amount &&
        formData.quantity > 0 &&
        formData.numberOfDays > 0 &&
        !taxExemptValidationError;

    return (
        <Paper sx={{ p: 3, maxWidth: 800 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
                Create Invoice
            </Typography>

            {submitError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                    {submitError}
                </Alert>
            )}

            {taxExemptValidationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    A memo is required when the invoice is tax exempt.
                </Alert>
            )}

            {submitSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess(null)}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Invoice created successfully!
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Invoice ID:</strong> {submitSuccess.invoiceId}
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Invoice Number:</strong> {submitSuccess.invoiceNumber}
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Subtotal:</strong> ${(submitSuccess.totalExcludingTax / 100).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Taxes:</strong> ${(submitSuccess.totalTaxes / 100).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                        <strong>Total:</strong> ${(submitSuccess.total / 100).toFixed(2)}
                    </Typography>
                    {submitSuccess.subscription && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Subscription Details
                            </Typography>
                            <Typography variant="body2" component="div">
                                <strong>Subscription ID:</strong> {submitSuccess.subscription.subscriptionId}
                            </Typography>
                            <Typography variant="body2" component="div">
                                <strong>Number of Payments:</strong> {submitSuccess.subscription.numberOfPayments}
                            </Typography>
                            <Typography variant="body2" component="div">
                                <strong>Subtotal:</strong> $
                                {(submitSuccess.subscription.totalExcludingTax / 100).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" component="div">
                                <strong>Taxes:</strong> ${(submitSuccess.subscription.totalTaxes / 100).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                <strong>Total (all payments):</strong> $
                                {(submitSuccess.subscription.total / 100).toFixed(2)}
                            </Typography>
                        </Box>
                    )}
                    {submitSuccess.paymentInstructions && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" component="div">
                                <strong>Payment Instructions:</strong>
                                {submitSuccess.paymentInstructions.split('\n').map((line, i) => (
                                    <Box key={i} sx={i > 0 ? { pl: 2 } : undefined}>
                                        {line}
                                    </Box>
                                ))}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <a href={submitSuccess.paymentUrl} target="_blank" rel="noopener noreferrer">
                            View Payment Page
                        </a>
                    </Box>
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange('email')}
                            required
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="First Name"
                            value={formData.firstName}
                            onChange={handleChange('firstName')}
                            required
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Last Name"
                            value={formData.lastName}
                            onChange={handleChange('lastName')}
                            required
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormControl size="small" required sx={{ minWidth: 120 }}>
                            <InputLabel id="locale-label">Language</InputLabel>
                            <Select
                                labelId="locale-label"
                                value={formData.locale}
                                label="Language"
                                onChange={handleSelectChange('locale')}
                            >
                                <MenuItem value="en-CA">English</MenuItem>
                                <MenuItem value="fr-CA">Français</MenuItem>
                            </Select>
                        </FormControl>

                        <CenterSelect
                            value={formData.centerSlugId}
                            onChange={(value, center) => {
                                setFormData((prev) => ({ ...prev, centerSlugId: value }));
                                setSelectedCenter(center);
                            }}
                            locale={formData.locale}
                            required
                        />

                        <TextField
                            label="Teacher"
                            value={formData.teacherName}
                            onChange={handleChange('teacherName')}
                            required
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel id="product-label">Product</InputLabel>
                            <Select
                                labelId="product-label"
                                value={formData.productSku || ''}
                                label="Product"
                                onChange={handleProductChange}
                                disabled={productsLoading}
                                endAdornment={
                                    productsLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : undefined
                                }
                            >
                                {products
                                    .map((product) => {
                                        const lang = formData.locale.split('-')[0];
                                        const name = product.translations[lang]?.name || product.productSku;
                                        const selectValue = product.productSku || '';
                                        return (
                                            <MenuItem key={selectValue} value={selectValue}>
                                                {name}
                                            </MenuItem>
                                        );
                                    })
                                    .sort((a, b) => a.props.children.localeCompare(b.props.children))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Total Amount (without tax)"
                            type="number"
                            value={formData.amount}
                            onChange={handleChange('amount')}
                            required
                            size="small"
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                },
                                htmlInput: { min: 0, step: '0.01' },
                            }}
                            sx={{ minWidth: 190 }}
                        />

                        {selectedProduct?.eventType === MfiEventType.treatment && (
                            <TextField
                                label="Sessions"
                                type="number"
                                value={formData.quantity}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))
                                }
                                required
                                size="small"
                                slotProps={{ htmlInput: { min: 1 } }}
                                sx={{ minWidth: 90, maxWidth: 90 }}
                            />
                        )}
                    </Box>

                    {selectedProduct && selectedProduct.numberOfPayments > 1 && (
                        <Alert severity="info">
                            For products with multiple payments, enter the <strong>total amount</strong>. Do{' '}
                            <strong>not</strong> add taxes and admin fees. The system will calculate the correct
                            installment amounts, taxes, and fees based on the product configuration.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormControl size="small" sx={{ minWidth: 200 }} required>
                            <InputLabel>Payment Method</InputLabel>
                            <Select
                                value={formData.paymentMethod}
                                label="Payment Method"
                                onChange={handlePaymentMethodChange}
                                disabled={availablePaymentMethods.length === 0}
                            >
                                {availablePaymentMethods.map((method) => (
                                    <MenuItem key={method} value={method}>
                                        {formatPaymentMethod(method)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Start Date"
                            type="date"
                            value={formData.startDate}
                            onChange={handleChange('startDate')}
                            required
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ minWidth: 140 }}
                        />

                        {selectedProduct?.eventType === MfiEventType.retreat && (
                            <TextField
                                label="Days"
                                type="number"
                                value={formData.numberOfDays}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, numberOfDays: parseInt(e.target.value) || 0 }))
                                }
                                required
                                size="small"
                                slotProps={{ htmlInput: { min: 1 } }}
                                sx={{ minWidth: 60, maxWidth: 60 }}
                            />
                        )}

                        {requiresVedicExpert && (
                            <TextField
                                label="Vedic Expert"
                                value={formData.vedicExpert}
                                onChange={handleChange('vedicExpert')}
                                required
                                fullWidth
                                size="small"
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        {role !== 'teacher' && (
                            <TextField
                                label="Lead Source"
                                value={formData.leadSource}
                                onChange={handleChange('leadSource')}
                                size="small"
                                placeholder="Optional"
                                sx={{ minWidth: 140 }}
                            />
                        )}

                        <TextField
                            label="Memo"
                            value={formData.memo}
                            onChange={handleChange('memo')}
                            fullWidth
                            size="small"
                            placeholder="Optional"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.taxExempt}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, taxExempt: e.target.checked }))}
                                />
                            }
                            label="Tax exempt (add memo with tax exemption reason)"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.sendEmail}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, sendEmail: e.target.checked }))}
                                    disabled={formData.paymentMethod === CaPaymentMethod.account}
                                />
                            }
                            label="Send invoice email to customer"
                        />
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid || submitting}
                        endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{ mt: 1 }}
                    >
                        {submitting ? 'Creating...' : 'Create Invoice'}
                    </Button>
                </Stack>
            </form>
        </Paper>
    );
}
